import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { ProjectTodo } from '../ducks';
import { RowState } from '../selectors';
import { ProjectRowContent } from './ProjectRowContent';
import { ProjectStatusCell } from './ProjectStatusCell';
import { OwnerAvatar } from '@/features/calendar/components/OwnerAvatar';
import { User } from '@/infra/types/domain';

interface InboxProjectRowProps {
  todo: ProjectTodo;
  depth: number;
  connectors: boolean[];
  hasChildren: boolean;
  collapsed: boolean;
  onToggleCollapsed: (id: string) => void;
  // Completion dispatch (mirrors TodoItem's onUpdate). Undefined → inert.
  onUpdate?: (update: { id: string; [key: string]: unknown }) => void;
  // Household members; the owner avatar renders only when there's >1 (so a
  // single-user household isn't tagged with its own avatar on every row).
  users: User[];
  // Classification computed once per project group and passed down.
  rowState: RowState;
  planned: boolean;
}

// Read-only adapter for the Inbox "Projects" sub-tab. Renders the shared
// ProjectRowContent (same skeleton as the editable Outline) with the read-only
// affordances: a status glyph for parents, a schedule-complete checkbox for
// leaves, and a Planned badge — no reorder/reparent, add-subtask, delete, or
// status toggle. The only drag is the native @dnd-kit schedule-drag onto the
// Todos page's Today/Tomorrow (or a partner column = claim), resolved by
// TodoList's onDragEnd via the `kind:'project-row'` data contract below.
export function InboxProjectRow({
  todo,
  depth: _depth,
  connectors,
  hasChildren,
  collapsed,
  onToggleCollapsed,
  onUpdate,
  users,
  rowState,
  planned,
}: InboxProjectRowProps) {
  const [, setSearchParams] = useSearchParams();

  // Only unblocked (ready) rows with no recurrence can be scheduled by drag.
  const draggable = rowState === 'ready' && !todo.recurrence_rule;

  // @dnd-kit draggable source. The schedule-drag drops onto the Todos page's
  // Today/Tomorrow droppables (own column) or a partner column (claim) — all
  // resolved by TodoList's onDragEnd via this data. `disabled` keeps blocked /
  // recurring rows inert. Hooks run unconditionally.
  // The @dnd-kit registration id is namespaced (`project:<id>`) because a
  // SCHEDULED project task (Planned badge) ALSO renders as a regular Today todo
  // (SortableTodoRow, id=<id>) inside the same DndContext — and dnd-kit ids must
  // be unique or the duplicate breaks pickup of that row. The household-wide
  // projects block makes this collide constantly in family view. Routing reads
  // data.id (the real todo id), so the prefix is invisible to resolveDrop.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `project:${todo.id}`,
    disabled: !draggable,
    data: { kind: 'project-row', id: todo.id, ownerId: todo.owner_id, todo },
  });

  const openDetail = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('todo', todo.id);
      return next;
    });
  };

  const onRowKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  const statusCell = (
    <ProjectStatusCell
      todo={todo}
      rowState={rowState}
      hasChildren={hasChildren}
      editable={false}
      onUpdate={onUpdate}
    />
  );

  const afterTitle =
    users.length > 1 ? <OwnerAvatar ownerId={todo.owner_id} users={users} size="sm" /> : null;

  // The visual row. When draggable, the @dnd-kit drag source wrapper (below)
  // owns click + drag and the inner row carries no interactive props (so the
  // 6px PointerSensor distinguishes click from drag on one element). When not
  // draggable, the row itself carries onClick/role/data-todo-id.
  const row = (
    <ProjectRowContent
      rowClassName={`todo-row--${rowState}`}
      rowProps={
        draggable
          ? undefined
          : {
              role: 'button',
              tabIndex: 0,
              onClick: openDetail,
              onKeyDown: onRowKeyDown,
              'data-todo-id': todo.id,
              'data-row-state': rowState,
            }
      }
      connectors={connectors}
      hasChildren={hasChildren}
      collapsed={collapsed}
      todoId={todo.id}
      onToggleCollapsed={onToggleCollapsed}
      statusCell={statusCell}
      title={todo.title}
      planned={planned}
      afterTitle={afterTitle}
    />
  );

  if (!draggable) return row;

  // Drag source wrapper. @dnd-kit's PointerSensor (6px distance) owns the drag;
  // a plain click still opens the detail. The whole surface is grabbable — no
  // pointer-events hacks, since pointerdown bubbles to this wrapper.
  return (
    <div
      ref={setNodeRef}
      className="inbox-project-drag"
      style={{ opacity: isDragging ? 0.4 : 1 }}
      data-todo-id={todo.id}
      data-row-state={rowState}
      onClick={openDetail}
      onKeyDown={onRowKeyDown}
      {...attributes}
      {...listeners}
    >
      {row}
    </div>
  );
}
